import axios from 'axios';
import { API_ROUTES, IMAGE_COMPRESSION, UserType } from './constants';

// New Relic browser agent
declare global {
  interface Window {
    newrelic?: {
      addToTrace: (name: string, attributes?: Record<string, any>) => void;
      recordCustomEvent: (name: string, attributes?: Record<string, any>) => void;
      noticeError: (error: Error, attributes?: Record<string, any>) => void;
      setCustomAttribute: (name: string, value: any) => void;
      setUserId: (userId: string) => void;
      interaction: () => {
        end: () => void;
        setCustomAttribute: (name: string, value: any) => void;
      };
    };
  }
}

// New Relic logging function
export function sendLog(title: string, data: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.newrelic) {
    window.newrelic!.recordCustomEvent(title, {
      ...data,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });
  }
}

// New Relic error logging
// Uses noticeError for proper error tracking in New Relic
export function sendError(error: Error, context: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      // noticeError is the proper method for error tracking
      window.newrelic!.noticeError(error, {
        ...context,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      });
    } catch (err) {
      // Fallback to console if New Relic fails
      console.error('New Relic error tracking failed:', err);
      console.error('Original error:', error);
    }
  }
}

// New Relic user tracking
export function setUser(userId: string, userData: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.newrelic) {
    window.newrelic!.setUserId(userId);
    Object.entries(userData).forEach(([key, value]) => {
      window.newrelic!.setCustomAttribute(key, value);
    });
  }
}

// TODO: Token getter function - will be set by Supabase auth context
let getAccessTokenFn: (() => Promise<string | null>) | null = null;

// Set the token getter function (called from Supabase auth context)
export function setTokenGetter(fn: () => Promise<string | null>) {
  getAccessTokenFn = fn;
}

// Always attach the access token to every request
axios.interceptors.request.use(async (config) => {
  // Check if this is an admin endpoint that requires authentication
  const isAdminEndpoint = config.url?.includes('/admin/');
  
  // Get access token if available (will be set by Supabase auth context)
  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn();
      if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
        console.log('Axios interceptor - Token attached to request:', {
          url: config.url,
          method: config.method,
          tokenLength: token.length,
          tokenPreview: token.substring(0, 20) + '...'
        });
      } else {
        // Log when token is not available for debugging
        console.warn('Axios interceptor - Access token not available for request:', {
          url: config.url,
          method: config.method,
          isAdminEndpoint
        });
        
        // For admin endpoints, this is a critical error
        if (isAdminEndpoint) {
          console.error('Axios interceptor - Admin endpoint requires authentication but no token available:', {
            url: config.url,
            method: config.method
          });
        }
      }
    } catch (error) {
      console.error('Axios interceptor - Error getting access token:', error);
      if (isAdminEndpoint) {
        console.error('Axios interceptor - Admin endpoint request will fail due to token error:', {
          url: config.url,
          method: config.method,
          error
        });
      }
    }
  } else {
    // Log when token getter is not set
    console.warn('Axios interceptor - Token getter function not set. Request may fail:', {
      url: config.url,
      method: config.method,
      isAdminEndpoint
    });
    
    // For admin endpoints, this is a critical error
    if (isAdminEndpoint) {
      console.error('Axios interceptor - Admin endpoint requires token getter but it is not set:', {
        url: config.url,
        method: config.method
      });
    }
  }
  
  // Add request ID for tracing
  const requestId = generateRequestId();
  config.headers = config.headers || {};
  config.headers['X-Request-Id'] = requestId;
  
  // Log API request
  sendLog('InfluencersFrontendAPIRequest', {
    url: config.url,
    method: config.method,
    requestId,
    hasTokenGetter: !!getAccessTokenFn,
    hasAuthHeader: !!config.headers['Authorization']
  });
  
  return config;
});

// Log API responses
axios.interceptors.response.use(
  (response) => {
    sendLog('InfluencersFrontendAPIResponse', {
      url: response.config.url,
      method: response.config.method,
      status: response.status,
      requestId: response.config.headers['X-Request-Id']
    });
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const isUnauthorized = status === 401 || status === 403;
    
    sendError(error, {
      url: error.config?.url,
      method: error.config?.method,
      status,
      requestId: error.config?.headers?.['X-Request-Id'],
      isUnauthorized
    });

    // Handle unauthorized errors by redirecting to login
    if (isUnauthorized && typeof window !== 'undefined') {
      // Store the current location so we can return after login
      const currentPath = window.location.pathname + window.location.search;
      
      // Only store if we're not already on the login page
      if (!currentPath.includes('/login') && !currentPath.includes('/auth/callback')) {
        sessionStorage.setItem('authReturnPath', currentPath);
        console.log('Unauthorized error detected, storing return path:', currentPath);
        
        // Redirect to login page
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Utility to get URL query parameters
export function getQueryParams(): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(window.location.search));
}

// Utility to store user data in sessionStorage
export function setUserData(userData: { id: string; first_name: string; last_name: string; userType?: UserType }) {
  sessionStorage.setItem('user_data', JSON.stringify(userData));
  sendLog('InfluencersFrontendUserDataStored', {
    userId: userData.id,
    firstName: userData.first_name,
    lastName: userData.last_name,
    userType: userData.userType
  });
}

// Utility to get user data from sessionStorage
export function getUserData(): { id: string; first_name: string; last_name: string; userType?: UserType } | null {
  const userData = sessionStorage.getItem('user_data');
  if (userData) {
    try {
      const parsed = JSON.parse(userData);
      sendLog('InfluencersFrontendUserDataRetrieved', {
        userId: parsed.id,
        firstName: parsed.first_name,
        lastName: parsed.last_name,
        userType: parsed.userType
      });
      return parsed;
    } catch (error) {
      console.error('Failed to parse user data from sessionStorage:', error);
      sessionStorage.removeItem('user_data');
    }
  }
  return null;
}

// Utility to remove user data from sessionStorage
export function removeUserData() {
  sessionStorage.removeItem('user_data');
  sendLog('InfluencersFrontendUserDataRemoved');
}

// Generate a random request ID (UUID v4 or short random string)
export function generateRequestId() {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}

// ============================================================================
// Image Compression
// ============================================================================

/**
 * Read EXIF Orientation tag from a JPEG file.
 * Returns 1 (no rotation) if tag is not found or file is not JPEG.
 */
async function getImageOrientation(file: File): Promise<number> {
  if (file.type !== 'image/jpeg') return 1;

  const buffer = await file.arrayBuffer();
  const view = new DataView(buffer);

  // JPEG files start with 0xFFD8
  if (view.getUint16(0) !== 0xFFD8) return 1;

  let offset = 2;
  const length = view.byteLength;

  while (offset < length - 1) {
    if (view.getUint8(offset) !== 0xFF) {
      offset++;
      continue;
    }

    const marker = view.getUint16(offset);
    offset += 2;

    // SOF0 — no more segments before image data
    if (marker === 0xFFC0) return 1;

    // Not an APP1 marker (where EXIF lives)
    if (marker !== 0xFFE1) {
      const segLength = view.getUint16(offset);
      offset += 2 + segLength;
      continue;
    }

    // Check for "Exif\0\0" signature
    const exifSig = String.fromCharCode(
      view.getUint8(offset + 2),
      view.getUint8(offset + 3),
      view.getUint8(offset + 4),
      view.getUint8(offset + 5)
    );
    if (exifSig !== 'Exif') {
      const segLength = view.getUint16(offset);
      offset += 2 + segLength;
      continue;
    }

    // Read IFD offset (4 bytes after Exif header)
    const ifdOffset = view.getUint32(offset + 6, false);
    const ifdPtr = offset + 2 + ifdOffset;

    // Number of IFD entries
    const numEntries = view.getUint16(ifdPtr, false);

    for (let i = 0; i < numEntries; i++) {
      const entryPtr = ifdPtr + 2 + i * 12;
      const tag = view.getUint16(entryPtr, false);

      // Orientation tag is 0x0112 (274)
      if (tag === 0x0112) {
        const value = view.getUint16(entryPtr + 8, false);
        return value;
      }
    }

    break;
  }

  return 1;
}

/**
 * Apply EXIF orientation transform to canvas context.
 */
function applyOrientation(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  orientation: number
): void {
  switch (orientation) {
    case 2:
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
      break;
    case 3:
      ctx.translate(width, height);
      ctx.rotate(Math.PI);
      break;
    case 4:
      ctx.translate(0, height);
      ctx.scale(1, -1);
      break;
    case 5:
      ctx.rotate(Math.PI / 2);
      ctx.scale(-1, 1);
      break;
    case 6:
      ctx.rotate(Math.PI / 2);
      ctx.translate(0, -width);
      break;
    case 7:
      ctx.rotate(Math.PI / 2);
      ctx.translate(height, -width);
      ctx.scale(-1, 1);
      break;
    case 8:
      ctx.rotate(-Math.PI / 2);
      ctx.translate(-height, 0);
      break;
    default: // 1 or unknown — no transform
      break;
  }
}

/**
 * Create a File from a compressed blob.
 */
function createCompressedFile(
  original: File,
  blob: Blob,
  type: string
): File {
  const compressedName = original.name.replace(/\.[^.]+$/, '.jpg');
  return new File([blob], compressedName, {
    type,
    lastModified: Date.now(),
  });
}

/**
 * Compress an image in the browser using Canvas API.
 * Resizes to fit within max dimensions while preserving aspect ratio,
 * then iteratively lowers JPEG quality until the output is at or below
 * maxSizeBytes. If quality alone is not enough, reduces dimensions.
 *
 * Returns the original file unchanged if:
 * - Compression is disabled in config
 * - The file is not a supported image type (e.g. HEIC, SVG, GIF)
 * - The file is already smaller than maxSizeBytes
 * - Canvas operations fail (graceful degradation)
 *
 * @param file - The original image File
 * @param options - Compression options
 * @returns Promise<File> - Compressed file or original if compression skipped
 */
export async function compressImage(
  file: File,
  options: {
    maxWidth?: number;
    maxHeight?: number;
    maxSizeBytes?: number;
    qualityStart?: number;
    qualityStep?: number;
    qualityMin?: number;
    minWidth?: number;
    minHeight?: number;
    outputFormat?: string;
    enabled?: boolean;
  } = {}
): Promise<File> {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    maxSizeBytes = 102400,
    qualityStart = 0.92,
    qualityStep = 0.12,
    qualityMin = 0.3,
    minWidth = 640,
    minHeight = 640,
    outputFormat = 'image/jpeg',
    enabled = true,
  } = options;

  // Fast path: compression disabled
  if (!enabled) return file;

  // Fast path: already under target
  if (file.size <= maxSizeBytes) return file;

  // Skip unsupported types
  const skipTypes = ['image/svg+xml', 'image/gif', 'image/heic', 'image/heif', 'image/tiff', 'image/bmp'];
  if (!file.type || skipTypes.includes(file.type)) {
    return file;
  }

  try {
    // Decode the image
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;

    // Read EXIF orientation to apply correct rotation
    const orientation = await getImageOrientation(file);

    // Calculate initial target dimensions (preserve aspect ratio)
    let targetWidth = width;
    let targetHeight = height;

    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      targetWidth = Math.round(width * ratio);
      targetHeight = Math.round(height * ratio);
    }

    let bestBlob: Blob | null = null;

    // Phase 1: Iterate quality at max dimensions
    let quality = qualityStart;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.save();
    applyOrientation(ctx, targetWidth, targetHeight, orientation);
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    ctx.restore();

    bitmap.close();

    while (quality >= qualityMin) {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, outputFormat, quality);
      });

      if (!blob) break;

      if (blob.size <= maxSizeBytes) {
        return createCompressedFile(file, blob, outputFormat);
      }

      if (!bestBlob || blob.size < bestBlob.size) {
        bestBlob = blob;
      }

      quality -= qualityStep;
    }

    // Phase 2: Quality alone was not enough — reduce dimensions
    let currentWidth = targetWidth;
    let currentHeight = targetHeight;

    while (currentWidth >= minWidth && currentHeight >= minHeight) {
      // Scale down by 15% each step
      currentWidth = Math.max(minWidth, Math.round(currentWidth * 0.85));
      currentHeight = Math.max(minHeight, Math.round(currentHeight * 0.85));

      canvas.width = currentWidth;
      canvas.height = currentHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Re-decode for new dimensions
      const resizedBitmap = await createImageBitmap(file);
      ctx.save();
      applyOrientation(ctx, currentWidth, currentHeight, orientation);
      ctx.drawImage(resizedBitmap, 0, 0, currentWidth, currentHeight);
      ctx.restore();
      resizedBitmap.close();

      // Try quality iteration at this size
      let quality = qualityStart;
      while (quality >= qualityMin) {
        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, outputFormat, quality);
        });

        if (!blob) break;

        if (blob.size <= maxSizeBytes) {
          return createCompressedFile(file, blob, outputFormat);
        }

        if (!bestBlob || blob.size < bestBlob.size) {
          bestBlob = blob;
        }

        quality -= qualityStep;
      }
    }

    // Could not reach target — return the smallest result we got
    if (bestBlob) {
      console.warn(
        `[compressImage] Could not reach ${maxSizeBytes}B target. ` +
        `Best result: ${bestBlob.size}B at ${currentWidth}x${currentHeight}. Original: ${file.size}B.`
      );
      return createCompressedFile(file, bestBlob, outputFormat);
    }

    // Everything failed — return original
    return file;
  } catch (err) {
    console.warn('[compressImage] Compression failed, returning original file:', err);
    return file;
  }
}

/**
 * Validate photo file size
 * @param file - The file to validate
 * @param maxSize - Maximum allowed size in bytes
 * @throws Error if file exceeds maximum size
 */
export function validatePhotoSize(file: File, maxSize: number): void {
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    throw new Error(`Photo size (${fileSizeMB} MB) exceeds maximum allowed size of ${maxSizeMB} MB`);
  }
}

/**
 * Upload an image directly to Oracle Object Storage bucket.
 * Compresses the image before upload using compressImage().
 * @param file - The image file to upload
 * @param userId - The user ID from Oracle DB (e.g., "xxx-yyy-zzz")
 * @param bucketConfig - Oracle Object Storage configuration from config.json
 * @returns Promise resolving to the object URL
 */
export async function uploadImageToOracleBucket(
  file: File,
  userId: string,
  bucketConfig: { region: string; namespace: string; bucketName: string; maxPhotoSize: number; par: string }
): Promise<string> {
  if (!bucketConfig.region || !bucketConfig.namespace || !bucketConfig.bucketName) {
    throw new Error('Oracle bucket configuration is incomplete');
  }

  // Compress the image before upload
  const processedFile = await compressImage(file, IMAGE_COMPRESSION);

  // Validate size of the (possibly compressed) file
  validatePhotoSize(processedFile, bucketConfig.maxPhotoSize);

  // Always use .jpg extension since we compress to JPEG
  const uniqueFilename = `img-${Date.now()}-${Math.random().toString(36).slice(2, 15)}.jpg`;
  const objectPath = `${userId}/profilePhotos/${uniqueFilename}`;

  const arrayBuffer = await processedFile.arrayBuffer();
  const fileContent = new Uint8Array(arrayBuffer);

  const objectStorageUrl = `${bucketConfig.par}${objectPath}`;
  const response = await fetch(objectStorageUrl, {
    method: 'PUT',
    body: fileContent,
    headers: {
      'Content-Type': processedFile.type || 'image/jpeg',
      'Cache-Control': 'public, max-age=31536000, immutable',
    }
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  return objectStorageUrl;
}

/**
 * Generate a temporary password for user signup
 * Note: Users will need to reset this password later
 * @returns A secure temporary password
 */
export function generateTemporaryPassword(): string {
  return `Temp${Math.random().toString(36).slice(2, 15)}${Math.random().toString(36).slice(2, 15)}!`;
}

/**
 * Generate a unique ID matching backend format
 * Format: {prefix}-{timestamp}-{randomPart}-{uuid}
 * @param prefix - The prefix for the ID (e.g., 'biz' for business, 'inf' for influencer)
 * @returns A unique ID string
 */
export function generateUniqueId(prefix: string = 'id'): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).slice(2, 14);
  // Generate UUID-like hex string (16 hex characters = 8 bytes)
  const uuid = Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return `${prefix}-${timestamp}-${randomPart}-${uuid}`;
}

/**
 * Remove a user from the admin users list cache
 * @param userId - The ID of the user to remove
 * @param tab - The tab (ADMIN_TABS.INFLUENCERS or ADMIN_TABS.BUSINESSES) from which to remove the user
 */
export function removeUserFromAdminCache(userId: string, tab: 'influencers' | 'businesses') {
  const CACHE_KEY = 'adminUsersListCache';
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const cache = JSON.parse(cached);
      if (cache[tab] && Array.isArray(cache[tab])) {
        cache[tab] = cache[tab].filter((user: any) => user.id !== userId);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
        
        // Dispatch custom event to notify AdminUsersListPage
        window.dispatchEvent(new CustomEvent('adminUserRemoved', {
          detail: { userId, tab }
        }));
      }
    }
  } catch (e) {
    console.error('Error removing user from admin cache:', e);
  }
}

/**
 * Get formatted country label with proper capitalization
 * @param countryValue - The country value (e.g., 'estonia', 'latvia')
 * @returns Formatted country name (e.g., 'Estonia', 'Latvia')
 */
/**
 * Capitalize first letter of each word
 */
function capitalizeWords(value: string): string {
  if (!value) return value;
  return value
    .split(/[\s_]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getCountryLabel(countryValue: string): string {
  if (!countryValue) return countryValue;
  
  // Special cases that need specific formatting
  const countryLabels: { [key: string]: string } = {
    united_kingdom: 'United Kingdom',
    united_states: 'United States'
  };
  
  const normalizedValue = countryValue.toLowerCase();
  const label = countryLabels[normalizedValue];
  
  return label || capitalizeWords(countryValue);
}

/**
 * User profile cache with dynamic TTL.
 * TTL = min(24 hours, remaining time until paidTillDate).
 * For users with no active subscription: 1 hour.
 * Minimum TTL: 5 minutes (avoid excessive re-fetches).
 */
interface ProfileCacheEntry {
  data: any;
  timestamp: number;
}

const PROFILE_CACHE_MAX_TTL = 24 * 60 * 60 * 1000; // 24 hours
const PROFILE_CACHE_NO_SUB_TTL = 1 * 60 * 60 * 1000; // 1 hour for expired users
const PROFILE_CACHE_MIN_TTL = 5 * 60 * 1000; // 5 minutes minimum
const PROFILE_CACHE_KEY_PREFIX = 'user_profile_cache_';

/**
 * Compute the effective TTL for a cached profile based on paidTillDate.
 * Returns the shorter of 24h or remaining time until subscription expiry.
 */
function computeProfileTTL(cacheEntry: ProfileCacheEntry, now: number): number {
  const paidTillDate = cacheEntry.data?.data?.paidTillDate;
  
  if (paidTillDate) {
    const paidTill = new Date(paidTillDate);
    paidTill.setUTCHours(23, 59, 59, 999); // End of the paidTillDate day
    const remaining = paidTill.getTime() - now;
    // If already expired (negative remaining), cache is immediately stale
    if (remaining <= 0) return 0;
    return Math.max(PROFILE_CACHE_MIN_TTL, Math.min(PROFILE_CACHE_MAX_TTL, remaining));
  }
  
  // No paidTillDate — user has no active subscription, use shorter TTL
  return PROFILE_CACHE_NO_SUB_TTL;
}

/**
 * Get cached user profile or fetch from API
 * @param apiUrl - The API base URL
 * @param userId - The user ID (optional, for cache key)
 * @param userType - The user type (optional, for cache key)
 * @returns Promise resolving to the user profile data
 */
export async function getCachedUserProfile(
  apiUrl: string,
  userId?: string,
  userType?: string
): Promise<any> {
  const cacheKey = `${PROFILE_CACHE_KEY_PREFIX}${userId || 'default'}_${userType || 'default'}`;
  
  // Check cache first
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const cacheEntry: ProfileCacheEntry = JSON.parse(cached);
      const now = Date.now();
      const age = now - cacheEntry.timestamp;
      
      // If cache is still valid, return cached data
      const ttl = computeProfileTTL(cacheEntry, now);
      if (age < ttl) {
        console.log('Using cached user profile', { userId, userType, age: Math.round(age / 1000) + 's', ttl: Math.round(ttl / 1000) + 's' });
        return cacheEntry.data;
      } else {
        // Cache expired, remove it
        sessionStorage.removeItem(cacheKey);
        console.log('User profile cache expired', { userId, userType, age: Math.round(age / 1000) + 's', ttl: Math.round(ttl / 1000) + 's' });
      }
    }
  } catch (error) {
    console.error('Error reading profile cache:', error);
    // Continue to fetch fresh data
  }

  // Fetch fresh data from API
  try {
    const response = await axios.get(`${apiUrl}${API_ROUTES.USER_PROFILE}`);
    const profileData = response.data;
    
    // Store in cache
    const cacheEntry: ProfileCacheEntry = {
      data: profileData,
      timestamp: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    console.log('Fetched and cached user profile', { userId, userType });
    
    return profileData;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
}

/**
 * Clear user profile cache
 * @param userId - The user ID (optional, clears all if not provided)
 * @param userType - The user type (optional)
 */
export function clearUserProfileCache(userId?: string, userType?: string) {
  if (userId && userType) {
    const cacheKey = `${PROFILE_CACHE_KEY_PREFIX}${userId}_${userType}`;
    sessionStorage.removeItem(cacheKey);
    console.log('Cleared user profile cache', { userId, userType });
  } else {
    // Clear all profile caches
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(PROFILE_CACHE_KEY_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
    console.log('Cleared all user profile caches');
  }
}

/**
 * Events cache with 10-minute TTL
 */
interface EventsCacheEntry {
  data: any[];
  timestamp: number;
}

const EVENTS_CACHE_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds
const EVENTS_CACHE_KEY_PREFIX = 'business_events_cache_';

/**
 * Get cached events or fetch from API
 * @param apiUrl - The API base URL
 * @param businessId - The business ID
 * @returns Promise resolving to the events array
 */
export async function getCachedBusinessEvents(
  apiUrl: string,
  businessId: string
): Promise<any[]> {
  const cacheKey = `${EVENTS_CACHE_KEY_PREFIX}${businessId}`;
  
  // Check cache first
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const cacheEntry: EventsCacheEntry = JSON.parse(cached);
      const now = Date.now();
      const age = now - cacheEntry.timestamp;
      
      // Log cache retrieval attempt
      console.log('Cache found:', {
        cacheKey,
        businessId,
        cachedEntryCount: cacheEntry.data?.length || 0,
        age: Math.round(age / 1000) + 's',
        ttl: Math.round(EVENTS_CACHE_TTL / 1000) + 's'
      });
      
      // If cache is still valid (less than 10 minutes old), verify and return cached data
      if (age < EVENTS_CACHE_TTL) {
        // Ensure data is an array
        if (!Array.isArray(cacheEntry.data)) {
          console.error('Cache data is not an array:', cacheEntry);
          sessionStorage.removeItem(cacheKey);
        } else {
          // Safety check: filter events to ensure they belong to the correct businessId
          const filteredEvents = cacheEntry.data.filter((event: any) => {
            const matches = event.businessId === businessId;
            if (!matches && event.businessId) {
              console.log('Event filtered out - businessId mismatch:', {
                eventId: event.id,
                eventBusinessId: event.businessId,
                requestedBusinessId: businessId
              });
            }
            return matches;
          });
          
          if (filteredEvents.length !== cacheEntry.data.length) {
            sendLog('BusinessEventsCacheDataMismatch', {
              businessId,
              cachedCount: cacheEntry.data.length,
              filteredCount: filteredEvents.length,
              age: Math.round(age / 1000) + 's'
            });
            // If there's a mismatch, invalidate cache and fetch fresh
            sessionStorage.removeItem(cacheKey);
          } else {
            sendLog('BusinessEventsCacheHit', {
              businessId,
              eventCount: filteredEvents.length,
              age: Math.round(age / 1000) + 's'
            });
            console.log('Returning cached events:', {
              count: filteredEvents.length,
              events: filteredEvents.map((e: any) => ({
                id: e.id,
                eventName: e.eventName,
                category: e.category,
                businessId: e.businessId
              }))
            });
            return filteredEvents;
          }
        }
      } else {
        // Cache expired, remove it
        sessionStorage.removeItem(cacheKey);
        sendLog('BusinessEventsCacheExpired', {
          businessId,
          age: Math.round(age / 1000) + 's'
        });
        console.log('Cache expired, removing:', cacheKey);
      }
    } else {
      console.log('No cache found for key:', cacheKey);
      // Check if there are any event caches at all
      const allKeys = Object.keys(sessionStorage);
      const eventCacheKeys = allKeys.filter(key => key.startsWith(EVENTS_CACHE_KEY_PREFIX));
      console.log('All event cache keys in sessionStorage:', eventCacheKeys);
    }
  } catch (error) {
    console.error('Error reading events cache:', error);
    // Continue to fetch fresh data
  }

  // Fetch fresh data from API
  try {
    console.log('Fetching events from API:', { apiUrl, businessId, endpoint: API_ROUTES.EVENTS });
    const response = await axios.get(`${apiUrl}${API_ROUTES.EVENTS}`, {
      params: { businessId }
    });
    let eventsData = response.data;
    
    console.log('API response received:', {
      businessId,
      responseDataType: Array.isArray(eventsData) ? 'array' : typeof eventsData,
      responseDataLength: Array.isArray(eventsData) ? eventsData.length : 'N/A',
      firstEvent: Array.isArray(eventsData) && eventsData.length > 0 ? eventsData[0] : null
    });
    
    // Ensure eventsData is an array
    if (!Array.isArray(eventsData)) {
      console.error('API response is not an array:', eventsData);
      eventsData = [];
    }
    
    // Safety check: filter events to ensure they belong to the correct businessId
    const filteredEvents = eventsData.filter((event: any) => {
      const matches = event.businessId === businessId;
      if (!matches) {
        console.log('API event filtered out - businessId mismatch:', {
          eventId: event.id,
          eventBusinessId: event.businessId,
          requestedBusinessId: businessId
        });
      }
      return matches;
    });
    
    if (filteredEvents.length !== eventsData.length) {
      sendLog('BusinessEventsAPIResponseDataMismatch', {
        businessId,
        receivedCount: eventsData.length,
        filteredCount: filteredEvents.length
      });
    }
    
    // Store in cache
    const cacheEntry: EventsCacheEntry = {
      data: filteredEvents,
      timestamp: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    console.log('Events cached:', {
      cacheKey,
      eventCount: filteredEvents.length,
      timestamp: cacheEntry.timestamp
    });
    sendLog('BusinessEventsFetchedAndCached', {
      businessId,
      eventCount: filteredEvents.length
    });
    
    return filteredEvents;
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
}

/**
 * Clear business events cache
 * @param businessId - The business ID (optional, clears all if not provided)
 */
export function clearBusinessEventsCache(businessId?: string) {
  if (businessId) {
    const cacheKey = `${EVENTS_CACHE_KEY_PREFIX}${businessId}`;
    sessionStorage.removeItem(cacheKey);
    sendLog('BusinessEventsCacheCleared', { businessId });
  } else {
    // Clear all event caches
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(EVENTS_CACHE_KEY_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
    sendLog('AllBusinessEventsCacheCleared');
  }
}

/**
 * Influencer events cache with session storage
 */
interface InfluencerEventsCacheEntry {
  data: any[];
  timestamp: number;
  city: string;
  country: string;
}

const INFLUENCER_EVENTS_CACHE_KEY = 'influencer_events_cache';

/**
 * Get cached influencer events or fetch from API
 * @param apiUrl - The API base URL
 * @param influencerId - The influencer ID
 * @param city - The influencer's city
 * @param country - The influencer's country
 * @returns Promise resolving to the events array
 */
export async function getCachedInfluencerEvents(
  apiUrl: string,
  influencerId: string,
  city: string,
  country: string
): Promise<any[]> {
  // Check cache first
  try {
    const cached = sessionStorage.getItem(INFLUENCER_EVENTS_CACHE_KEY);
    if (cached) {
      const cacheEntry: InfluencerEventsCacheEntry = JSON.parse(cached);
      
      // Verify cache is for the same location
      if (
        cacheEntry.city.toLowerCase() === city.toLowerCase() &&
        cacheEntry.country.toLowerCase() === country.toLowerCase()
      ) {
        sendLog('InfluencerEventsCacheHit', {
          influencerId,
          city,
          country,
          eventCount: cacheEntry.data.length,
          cachedAt: new Date(cacheEntry.timestamp).toISOString()
        });
        return cacheEntry.data;
      } else {
        // Location changed, clear old cache
        sessionStorage.removeItem(INFLUENCER_EVENTS_CACHE_KEY);
        sendLog('InfluencerEventsCacheLocationMismatch', {
          influencerId,
          requestedCity: city,
          requestedCountry: country,
          cachedCity: cacheEntry.city,
          cachedCountry: cacheEntry.country
        });
      }
    }
  } catch (error) {
    console.error('Error reading influencer events cache:', error);
    // Continue to fetch fresh data
  }

  // Fetch fresh data from API
  try {
    const response = await axios.get(`${apiUrl}${API_ROUTES.EVENTS}`, {
      params: { 
        city,
        country
      }
    });
    const eventsData = response.data;
    
    // Store in cache
    const cacheEntry: InfluencerEventsCacheEntry = {
      data: eventsData,
      timestamp: Date.now(),
      city,
      country
    };
    sessionStorage.setItem(INFLUENCER_EVENTS_CACHE_KEY, JSON.stringify(cacheEntry));
    sendLog('InfluencerEventsFetchedAndCached', {
      influencerId,
      city,
      country,
      eventCount: eventsData.length
    });
    
    return eventsData;
  } catch (error) {
    console.error('Error fetching influencer events:', error);
    throw error;
  }
}

/**
 * Clear influencer events cache
 */
export function clearInfluencerEventsCache() {
  sessionStorage.removeItem(INFLUENCER_EVENTS_CACHE_KEY);
  sendLog('InfluencerEventsCacheCleared');
}

/**
 * Event applications cache with session storage
 */
interface ApplicationsCacheEntry {
  data: any[];
  timestamp: number;
  eventId: string;
}

const APPLICATIONS_CACHE_KEY_PREFIX = 'event_applications_cache_';
const APPLICATIONS_CACHE_TTL = 30000; // 30 seconds

/**
 * Get cached event applications or fetch from API
 * Fetches applications, then batch fetches influencer profiles and merges them
 * @param apiUrl - The API base URL
 * @param eventId - The event ID
 * @returns Promise resolving to the applications array with merged influencer data
 */
export async function getCachedEventApplications(
  apiUrl: string,
  eventId: string
): Promise<any[]> {
  const cacheKey = `${APPLICATIONS_CACHE_KEY_PREFIX}${eventId}`;
  
  // Check cache first
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const cacheEntry: ApplicationsCacheEntry = JSON.parse(cached);
      
      // Verify cache is for the same event and not expired
      if (cacheEntry.eventId === eventId && (Date.now() - cacheEntry.timestamp) < APPLICATIONS_CACHE_TTL) {
        sendLog('EventApplicationsCacheHit', {
          eventId,
          applicationCount: cacheEntry.data.length,
          cachedAt: new Date(cacheEntry.timestamp).toISOString()
        });
        return cacheEntry.data;
      } else if (cacheEntry.eventId === eventId) {
        sendLog('EventApplicationsCacheExpired', {
          eventId,
          age: Date.now() - cacheEntry.timestamp,
          ttl: APPLICATIONS_CACHE_TTL
        });
      }
    }
  } catch (error) {
    console.error('Error reading applications cache:', error);
    // Continue to fetch fresh data
  }

  // Fetch fresh data from API
  try {
    // Step 1: Fetch applications
    const applicationsResponse = await axios.get(`${apiUrl}${API_ROUTES.APPLICATIONS}`, {
      params: { 
        eventId
      }
    });
    const applicationsData = applicationsResponse.data;
    
    sendLog('EventApplicationsFetched', {
      eventId,
      applicationCount: applicationsData.length
    });

    // Step 2: Extract unique influencer IDs
    const influencerIds = Array.from(new Set(applicationsData.map((app: any) => app.influencerId)));
    
    if (influencerIds.length === 0) {
      // No influencers to fetch, cache applications as is
      const cacheEntry: ApplicationsCacheEntry = {
        data: applicationsData,
        timestamp: Date.now(),
        eventId
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      return applicationsData;
    }

    sendLog('FetchingInfluencerProfilesBatch', {
      eventId,
      influencerCount: influencerIds.length
    });

    // Step 3: Batch fetch influencer profiles
    const influencersResponse = await axios.post(`${apiUrl}${API_ROUTES.INFLUENCERS_BATCH}`, {
      ids: influencerIds
    });
    const influencersData = influencersResponse.data;

    sendLog('InfluencerProfilesFetched', {
      eventId,
      requested: influencerIds.length,
      received: influencersData.length
    });

    // Step 4: Create a map of influencers by ID for quick lookup
    const influencersMap = influencersData.reduce((map: any, influencer: any) => {
      map[influencer.id] = influencer;
      return map;
    }, {});

    // Step 5: Merge applications with influencer data
    const mergedApplications = applicationsData.map((app: any) => ({
      ...app,
      influencer: influencersMap[app.influencerId] || null
    }));

    sendLog('ApplicationsAndInfluencersMerged', {
      eventId,
      mergedCount: mergedApplications.length,
      withInfluencerData: mergedApplications.filter((a: any) => a.influencer !== null).length
    });
    
    // Step 6: Store merged data in cache
    const cacheEntry: ApplicationsCacheEntry = {
      data: mergedApplications,
      timestamp: Date.now(),
      eventId
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    
    return mergedApplications;
  } catch (error) {
    console.error('Error fetching event applications:', error);
    throw error;
  }
}

/**
 * Clear event applications cache
 * @param eventId - The event ID (optional, clears all if not provided)
 */
export function clearEventApplicationsCache(eventId?: string) {
  if (eventId) {
    const cacheKey = `${APPLICATIONS_CACHE_KEY_PREFIX}${eventId}`;
    sessionStorage.removeItem(cacheKey);
    sendLog('EventApplicationsCacheCleared', { eventId });
  } else {
    // Clear all application caches
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(APPLICATIONS_CACHE_KEY_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
    sendLog('AllEventApplicationsCacheCleared');
  }
}

/**
 * Clear events cache for all businesses
 * Call this when application status changes to force fresh applicationStats
 */
export function clearEventsCache(): void {
  const keys = Object.keys(sessionStorage);
  keys.forEach(key => {
    if (key.startsWith(EVENTS_CACHE_KEY_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  });
  sendLog('EventsCacheCleared');
}

/**
 * Update application status in cache
 * @param eventId - The event ID
 * @param applicationId - The application ID to update
 * @param newStatus - The new status (use APPLICATION_STATUS constants)
 * @returns boolean - true if update was successful, false otherwise
 */
export function updateApplicationStatusInCache(
  eventId: string,
  applicationId: string,
  newStatus: string
): boolean {
  const cacheKey = `${APPLICATIONS_CACHE_KEY_PREFIX}${eventId}`;
  
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) {
      return false;
    }
    
    const cacheEntry: ApplicationsCacheEntry = JSON.parse(cached);
    
    // Verify cache is for the same event
    if (cacheEntry.eventId !== eventId) {
      return false;
    }
    
    // Find and update the application
    const applicationIndex = cacheEntry.data.findIndex((app: any) => app.id === applicationId);
    if (applicationIndex === -1) {
      return false;
    }
    
    // Update the status
    cacheEntry.data[applicationIndex].status = newStatus;
    cacheEntry.data[applicationIndex].updatedAt = new Date().toISOString();
    
    // Save back to cache
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    
    // Also clear events cache so applicationStats reflect the new status
    clearEventsCache();
    
    sendLog('ApplicationStatusUpdatedInCache', {
      eventId,
      applicationId,
      newStatus
    });
    
    return true;
  } catch (error) {
    console.error('Error updating application status in cache:', error);
    return false;
  }
}

/**
 * Get formatted city label with proper capitalization
 * @param cityValue - The city value (e.g., 'parnu', 'tallinn')
 * @returns Formatted city name (e.g., 'Pärnu', 'Tallinn')
 */
export function getCityLabel(cityValue: string): string {
  if (!cityValue) return cityValue;
  
  // Special cases that need specific formatting (e.g., special characters, multi-word)
  const cityLabels: { [key: string]: string } = {
    parnu: 'Pärnu',
    malmo: 'Malmö',
    new_york: 'New York',
    los_angeles: 'Los Angeles'
  };
  
  const normalizedValue = cityValue.toLowerCase();
  const label = cityLabels[normalizedValue];
  
  return label || capitalizeWords(cityValue);
}

/**
 * Convert time string (HH:mm am/pm) and date to Unix timestamp (milliseconds)
 * @param dateStr - Date string (e.g., "2024-01-15" or "25 Jul, 2025")
 * @param timeStr - Time string (e.g., "2:30 pm" or "14:30")
 * @returns Unix timestamp in milliseconds, or null if invalid
 */
export function convertTimeToTimestamp(dateStr: string, timeStr: string): number | null {
  if (!dateStr || !timeStr) {
    return null;
  }

  try {
    // Parse time string - handle both "HH:mm am/pm" and "HH:mm" formats
    let hours: number, minutes: number;
    const timeStrClean = timeStr.trim().toLowerCase();
    
    // Check if it's already a timestamp (integer)
    if (typeof timeStr === 'number' || (!isNaN(parseInt(timeStr)) && !timeStr.includes(':'))) {
      return parseInt(timeStr);
    }

    // Handle "HH:mm am/pm" format
    if (timeStrClean.includes('am') || timeStrClean.includes('pm')) {
      const [timePart, period] = timeStrClean.split(' ');
      const [h, m] = timePart.split(':').map(Number);
      
      hours = h;
      if (period === 'pm' && hours !== 12) {
        hours += 12;
      } else if (period === 'am' && hours === 12) {
        hours = 0;
      }
      minutes = m || 0;
    } else {
      // Handle "HH:mm" format (24-hour)
      const [h, m] = timeStrClean.split(':').map(Number);
      hours = h;
      minutes = m || 0;
    }

    // Parse date string - handle different formats
    let dateObj: Date;
    if (dateStr.includes(',')) {
      // Handle "25 Jul, 2025" format
      const dateParts = dateStr.match(/(\d+)\s+(\w+),\s+(\d+)/);
      if (dateParts) {
        const months: { [key: string]: number } = {
          'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
          'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
        };
        const month = months[dateParts[2]];
        if (month !== undefined) {
          dateObj = new Date(parseInt(dateParts[3]), month, parseInt(dateParts[1]));
        } else {
          dateObj = new Date(dateStr);
        }
      } else {
        dateObj = new Date(dateStr);
      }
    } else {
      // Handle ISO format or other standard formats
      dateObj = new Date(dateStr);
    }

    if (isNaN(dateObj.getTime())) {
      return null;
    }

    // Set hours and minutes
    dateObj.setHours(hours, minutes, 0, 0);
    
    // Return timestamp in milliseconds
    return dateObj.getTime();
  } catch (err) {
    console.error('Error converting time to timestamp:', err);
    return null;
  }
}

/**
 * Convert Unix timestamp (milliseconds) to time string (HH:mm am/pm)
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Time string in "HH:mm am/pm" format, or empty string if invalid
 */
export function convertTimestampToTimeString(timestamp: number | string | null | undefined): string {
  if (!timestamp) {
    return '';
  }

  try {
    // Convert string to number if needed
    let numericTimestamp: number;
    if (typeof timestamp === 'string') {
      numericTimestamp = parseInt(timestamp, 10);
      if (isNaN(numericTimestamp)) {
        return '';
      }
    } else if (typeof timestamp === 'number') {
      numericTimestamp = timestamp;
    } else {
      return '';
    }

    const date = new Date(numericTimestamp);
    if (isNaN(date.getTime())) {
      return '';
    }

    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'pm' : 'am';
    
    // Convert to 12-hour format
    if (hours === 0) {
      hours = 12;
    } else if (hours > 12) {
      hours -= 12;
    }

    const minutesStr = minutes.toString().padStart(2, '0');
    return `${hours}:${minutesStr} ${period}`;
  } catch (err) {
    console.error('Error converting timestamp to time string:', err);
    return '';
  }
}

/**
 * Format two timestamps as a time range string (HH:mm am/pm - HH:mm am/pm)
 * @param startTimestamp - Start time timestamp in milliseconds
 * @param endTimestamp - End time timestamp in milliseconds
 * @returns Time range string, or empty string if invalid
 */
export function formatTimeRange(startTimestamp: number | null | undefined, endTimestamp: number | null | undefined): string {
  const startTime = convertTimestampToTimeString(startTimestamp);
  const endTime = convertTimestampToTimeString(endTimestamp);
  
  if (!startTime && !endTime) {
    return '';
  }
  
  if (startTime && endTime) {
    return `${startTime} - ${endTime}`;
  }
  
  return startTime || endTime;
}

// ===========================================
// Favorites Management (Frontend Only)
// ===========================================

const FAVORITES_STORAGE_KEY = 'influencer_favorites';

interface FavoriteItem {
  eventId: string;
  influencerId: string;
  timestamp: number;
}

/**
 * Get all favorites for an influencer from sessionStorage
 */
export function getFavorites(influencerId: string): string[] {
  try {
    const stored = sessionStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!stored) {
      return [];
    }
    
    const allFavorites: FavoriteItem[] = JSON.parse(stored);
    return allFavorites
      .filter(fav => fav.influencerId === influencerId)
      .map(fav => fav.eventId);
  } catch (error) {
    console.error('Error reading favorites from sessionStorage:', error);
    return [];
  }
}

/**
 * Add event to favorites in sessionStorage
 */
export function addToFavorites(influencerId: string, eventId: string): void {
  try {
    const stored = sessionStorage.getItem(FAVORITES_STORAGE_KEY);
    const allFavorites: FavoriteItem[] = stored ? JSON.parse(stored) : [];
    
    // Check if already favorited
    const exists = allFavorites.some(
      fav => fav.influencerId === influencerId && fav.eventId === eventId
    );
    
    if (!exists) {
      allFavorites.push({
        eventId,
        influencerId,
        timestamp: Date.now()
      });
      sessionStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(allFavorites));
      
      sendLog('FavoriteAdded', {
        influencerId,
        eventId,
        storage: 'sessionStorage'
      });
    }
  } catch (error) {
    console.error('Error adding favorite to sessionStorage:', error);
    sendError(error instanceof Error ? error : new Error(String(error)), {
      context: 'addToFavorites',
      influencerId,
      eventId
    });
  }
}

/**
 * Remove event from favorites in sessionStorage
 */
export function removeFromFavorites(influencerId: string, eventId: string): void {
  try {
    const stored = sessionStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!stored) {
      return;
    }
    
    const allFavorites: FavoriteItem[] = JSON.parse(stored);
    const filtered = allFavorites.filter(
      fav => !(fav.influencerId === influencerId && fav.eventId === eventId)
    );
    
    sessionStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(filtered));
    
    sendLog('FavoriteRemoved', {
      influencerId,
      eventId,
      storage: 'sessionStorage'
    });
  } catch (error) {
    console.error('Error removing favorite from sessionStorage:', error);
    sendError(error instanceof Error ? error : new Error(String(error)), {
      context: 'removeFromFavorites',
      influencerId,
      eventId
    });
  }
}

/**
 * Check if an event is favorited
 */
export function isEventFavorited(influencerId: string, eventId: string): boolean {
  const favorites = getFavorites(influencerId);
  return favorites.includes(eventId);
}

/**
 * Clear all favorites for an influencer
 */
export function clearFavorites(influencerId: string): void {
  try {
    const stored = sessionStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!stored) {
      return;
    }
    
    const allFavorites: FavoriteItem[] = JSON.parse(stored);
    const filtered = allFavorites.filter(fav => fav.influencerId !== influencerId);
    
    sessionStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(filtered));
    
    sendLog('FavoritesCleared', { influencerId });
  } catch (error) {
    console.error('Error clearing favorites from sessionStorage:', error);
  }
}

// ===========================================
// Influencer Applications Cache Management
// ===========================================

const INFLUENCER_APPLICATIONS_CACHE_KEY = 'influencer_applications_cache';

interface InfluencerApplicationsCacheEntry {
  data: any[];
  timestamp: number;
  influencerId: string;
}

/**
 * Get cached applications for an influencer or fetch from API
 * Returns applications with full event details merged
 */
export async function getCachedInfluencerApplications(
  apiUrl: string,
  influencerId: string
): Promise<any[]> {
  const cacheKey = INFLUENCER_APPLICATIONS_CACHE_KEY;
  
  // Check cache first
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const cacheEntry: InfluencerApplicationsCacheEntry = JSON.parse(cached);
      
      // Verify cache is for the same influencer
      if (cacheEntry.influencerId === influencerId) {
        sendLog('InfluencerApplicationsCacheHit', {
          influencerId,
          applicationCount: cacheEntry.data.length,
          cachedAt: new Date(cacheEntry.timestamp).toISOString()
        });
        return cacheEntry.data;
      } else {
        // Different influencer, clear old cache
        sessionStorage.removeItem(cacheKey);
        sendLog('InfluencerApplicationsCacheInfluencerMismatch', {
          influencerId,
          cachedInfluencerId: cacheEntry.influencerId
        });
      }
    }
  } catch (error) {
    console.error('Error reading influencer applications cache:', error);
    // Continue to fetch fresh data
  }

  // Fetch fresh data from API
  try {
    // Fetch applications for this influencer
    const applicationsResponse = await axios.get(`${apiUrl}${API_ROUTES.APPLICATIONS}`);
    const applicationsData = applicationsResponse.data;
    
    sendLog('InfluencerApplicationsFetched', {
      influencerId,
      applicationCount: applicationsData.length
    });

    if (applicationsData.length === 0) {
      // No applications, cache empty array
      const cacheEntry: InfluencerApplicationsCacheEntry = {
        data: [],
        timestamp: Date.now(),
        influencerId
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      return [];
    }

    // Extract unique event IDs
    const eventIds = Array.from(new Set(applicationsData.map((app: any) => app.eventId)));
    
    sendLog('FetchingEventDetailsBatch', {
      influencerId,
      eventCount: eventIds.length
    });

    // Batch fetch event details
    const eventPromises = eventIds.map((eventId: any) =>
      axios.get(`${apiUrl}${API_ROUTES.EVENTS}/${eventId}`).catch(error => {
        console.error(`Error fetching event ${eventId}:`, error);
        return null;
      })
    );
    
    const eventResponses = await Promise.all(eventPromises);
    const eventsData = eventResponses
      .filter(response => response !== null)
      .map(response => response!.data);

    sendLog('EventDetailsFetched', {
      influencerId,
      requested: eventIds.length,
      received: eventsData.length
    });

    // Create a map of events by ID for quick lookup
    const eventsMap = eventsData.reduce((map: any, event: any) => {
      map[event.id] = event;
      return map;
    }, {});

    // Merge applications with event data
    const mergedApplications = applicationsData.map((app: any) => ({
      ...app,
      event: eventsMap[app.eventId] || null
    }));

    sendLog('ApplicationsAndEventsMerged', {
      influencerId,
      mergedCount: mergedApplications.length,
      withEventData: mergedApplications.filter((a: any) => a.event !== null).length
    });
    
    // Store merged data in cache
    const cacheEntry: InfluencerApplicationsCacheEntry = {
      data: mergedApplications,
      timestamp: Date.now(),
      influencerId
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    
    return mergedApplications;
  } catch (error: any) {
    sendError(error instanceof Error ? error : new Error(String(error)), {
      context: 'getCachedInfluencerApplications',
      influencerId,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Clear influencer applications cache
 */
export function clearInfluencerApplicationsCache(): void {
  sessionStorage.removeItem(INFLUENCER_APPLICATIONS_CACHE_KEY);
  sendLog('InfluencerApplicationsCacheCleared');
}

/**
 * Filter applications to show only time-relevant ones
 * (events that haven't started yet or are within a reasonable time window)
 */
export function filterRelevantApplications(applications: any[]): any[] {
  const now = Date.now();
  
  return applications.filter((app: any) => {
    if (!app.event || !app.event.startTime) {
      return false; // Skip applications without event data
    }
    
    // Show if event hasn't started yet (startTime is in the future)
    // or if event started less than 24 hours ago (for recently completed events)
    const eventStartTime = app.event.startTime;
    const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
    
    return eventStartTime >= twentyFourHoursAgo;
  });
}

/**
 * Check if a user's subscription has expired based on paidTillDate.
 * Returns true if no paidTillDate or if the date is in the past.
 * Compares at UTC midnight to avoid timezone issues.
 */
export function isSubscriptionExpired(paidTillDate: string | null | undefined): boolean {
  if (!paidTillDate) return true;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const paidTill = new Date(paidTillDate);
  paidTill.setUTCHours(0, 0, 0, 0);
  return paidTill <= today;
}
